/* eslint-disable */
// @ts-nocheck
/* ============================================================================
 * MTE — BanqueItem discriminated union (Track J.3 — 2026-09-14)
 * ----------------------------------------------------------------------------
 * Source-of-truth (provisory) : shared/mte-product/03-skill-taxonomy.md
 * Owner : Atelier (Track J). Migration target = banque-questions-canon.ts
 *         produced by Lumen (Track J.1) — once available, replace this file.
 *
 * Scope (J.3a — 3 archétypes) :
 *   - texte-a-trou (TexteATrou component)
 *   - ordre        (Ordre component)
 *   - association  (Association component)
 *
 * Scope (J.3b — 4 archétypes) :
 *   - manipulation-glisser (ManipulationGlisser component)
 *   - production-ecrite    (ProductionEcrite component)
 *   - dictee               (Dictee component)
 *   - lecture-voix-haute   (LectureVoixHaute component)
 *
 * Existing archétypes (covered by QCM/VF/saisie canonical components) :
 *   - qcm, vf, saisie  → covered by MissionEtape (existing).
 *
 * Discriminated union by `archetype` enables type-safe rendering per UI.
 * ========================================================================== */

export type EvalMode = 'AUTO' | 'HUMAN';

export type Archetype =
  | 'qcm'
  | 'vf'
  | 'saisie'
  | 'texte-a-trou'
  | 'ordre'
  | 'association'
  | 'manipulation-glisser'
  | 'production-ecrite'
  | 'dictee'
  | 'lecture-voix-haute';

export interface BanqueItemBase {
  id: string;
  archetype: Archetype;
  competence: string;          // ex: "FR-CM1-CG-04"
  schoolLevel: 'CP' | 'CE1' | 'CE2' | 'CM1' | 'CM2';
  enonce: string;              // question / prompt affiché à l'enfant
  consigne?: string;           // optional micro-instruction (e.g. "Réécris le verbe")
  evalMode: EvalMode;          // AUTO: machine-checkable, HUMAN: parent/teacher
  meta?: Record<string, unknown>;
}

/** QCM (existing — covered by MissionEtape). */
export interface BanqueItemQcm extends BanqueItemBase {
  archetype: 'qcm';
  options: string[];
  bonneReponse: string;
}

/** Vrai/Faux (existing). */
export interface BanqueItemVf extends BanqueItemBase {
  archetype: 'vf';
  bonneReponse: boolean;
}

/** Saisie libre (existing). */
export interface BanqueItemSaisie extends BanqueItemBase {
  archetype: 'saisie';
  bonneReponse: string;        // normalized (lowercase, trim)
  tolerance?: number;          // 0 = exact, 1 = 1 typo tolerated
}

/**
 * Texte à trou — cloze input.
 * The original `enonce` is rendered with `trous` replaced by <input> elements.
 * Each trou has a unique `id` (within item) and `bonneReponse` (case-insensitive
 * trimmed match for J.3a baseline).
 */
export interface BanqueItemTexteATrou extends BanqueItemBase {
  archetype: 'texte-a-trou';
  /** Segments alternating literal/placeholder, e.g. ["Le chat ", "{T1}", " la souris."]
   *  Use the placeholder token {T<n>} (1-indexed) to mark a trou.
   */
  enonceSegments: string[];
  trous: Trou[];
}

export interface Trou {
  id: string;                  // "T1" / "T2" ...
  bonneReponse: string;        // normalized (lowercase, trim)
  tolerance?: number;          // 0 = exact, 1 = 1 typo
  placeholder?: string;        // input placeholder hint (no answer leak)
}

/**
 * Ordre — drag-to-reorder (or keyboard arrows).
 * The child must place the items in the correct order.
 * `ordreCorrect` = array of item IDs in canonical order.
 * `itemsOrdre` = array of {id, label} presented to the child (shuffled).
 */
export interface BanqueItemOrdre extends BanqueItemBase {
  archetype: 'ordre';
  itemsOrdre: Array<{ id: string; label: string }>;
  ordreCorrect: string[];      // array of item.id in canonical order
}

/**
 * Association — drag-pairs (linker items gauche → droite).
 * `pairesGauche` = items to drag.
 * `pairesDroite` = drop targets.
 * `pairesCorrectes` = mapping gauche.id → droite.id.
 */
export interface BanqueItemAssociation extends BanqueItemBase {
  archetype: 'association';
  pairesGauche: Array<{ id: string; label: string }>;
  pairesDroite: Array<{ id: string; label: string }>;
  pairesCorrectes: Record<string, string>;   // gauche.id -> droite.id
}

/** J.3b placeholders — added in J.3b commit. Kept here for type-safety. */

export interface BanqueItemManipulationGlisser extends BanqueItemBase {
  archetype: 'manipulation-glisser';
  /** Free-form metadata — UI may render fraction-on-numberline, geometry, etc. */
  spec: Record<string, unknown>;
}

export interface BanqueItemProductionEcrite extends BanqueItemBase {
  archetype: 'production-ecrite';
  minWords: number;
  placeholder?: string;
  evalMode: 'HUMAN';           // always parent/teacher evaluation
}

export interface BanqueItemDictee extends BanqueItemBase {
  archetype: 'dictee';
  audioUrl: string;            // canonical asset path (e.g. /assets/dictee/word-01.ogg)
  bonneReponse: string;        // canonical text expected
  replayMax: number;           // 3-5 per spec
}

export interface BanqueItemLectureVoixHaute extends BanqueItemBase {
  archetype: 'lecture-voix-haute';
  textALire: string;
  dureeMaxSecondes: number;
  evalMode: 'HUMAN';           // parent fallback if MediaRecorder fails
}

export type BanqueItem =
  | BanqueItemQcm
  | BanqueItemVf
  | BanqueItemSaisie
  | BanqueItemTexteATrou
  | BanqueItemOrdre
  | BanqueItemAssociation
  | BanqueItemManipulationGlisser
  | BanqueItemProductionEcrite
  | BanqueItemDictee
  | BanqueItemLectureVoixHaute;

/** Canonical answer normalizer (case + whitespace tolerant). */
export function normalizeAnswer(answer: string): string {
  return answer.toLowerCase().normalize('NFC').trim().replace(/\s+/g, ' ');
}

/** Compare a user answer to the expected answer with optional tolerance (Levenshtein). */
export function isAnswerCorrect(user: string, expected: string, tolerance = 0): boolean {
  const u = normalizeAnswer(user);
  const e = normalizeAnswer(expected);
  if (u === e) return true;
  if (tolerance <= 0) return false;
  return levenshtein(u, e) <= tolerance;
}

/** Compact Levenshtein for tolerant match (small strings only). */
export function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  const m = a.length;
  const n = b.length;
  if (!m) return n;
  if (!n) return m;
  const prev = new Array<number>(n + 1);
  const curr = new Array<number>(n + 1);
  for (let j = 0; j <= n; j++) prev[j] = j;
  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    for (let j = 1; j <= n; j++) {
      const cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
      curr[j] = Math.min(
        curr[j - 1] + 1,         // insertion
        prev[j] + 1,             // deletion
        prev[j - 1] + cost,      // substitution
      );
    }
    for (let j = 0; j <= n; j++) prev[j] = curr[j];
  }
  return prev[n];
}